import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { Box, TextField } from '@mui/material'
import { Connection, ProviderType } from 'features/connections'
import DocLink from 'features/common/ui/utils/DocLink'
import { DOCS_AT_LOCATION } from 'app/constants/urls'
import { Providers } from 'features/connections/providers'

interface AnalyticsToolboxFormProps {
  form: Omit<UseFormReturn, 'handleSubmit' | 'formState'>
  provider: ProviderType
  connection?: Connection
}

const analyticsToolboxName = 'atLocation'
function AnalyticsToolboxLocationForm({ form, connection, provider: providerId }: AnalyticsToolboxFormProps) {
  const { register, trigger, setValue, getFieldState } = form
  const { label: provider } = useMemo(() => Providers[providerId], [providerId])
  const providerDocLink = useMemo(() => DOCS_AT_LOCATION[providerId], [providerId])
  const [analyticsToolbox, _setAnalyticsToolbox] = useState<string>('')
  const error = getFieldState(analyticsToolboxName).invalid
  const isEditing = Boolean(connection?.id)
  const required = isEditing

  const setAnalyticsToolbox = useCallback(
    (value) => {
      _setAnalyticsToolbox(value)
      setValue(analyticsToolboxName, value, { shouldDirty: true })
      trigger(analyticsToolboxName)
    },
    [setValue, _setAnalyticsToolbox, trigger]
  )

  useEffect(() => {
    if (isEditing) {
      _setAnalyticsToolbox(connection?.atLocation || '')
      setValue(analyticsToolboxName, connection?.atLocation || '')
      trigger(analyticsToolboxName)
    }
  }, [connection?.atLocation, isEditing, setAnalyticsToolbox, setValue, trigger])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAnalyticsToolbox(e.target.value)
  }

  return (
    <>
      <input type="hidden" value={analyticsToolbox} {...register(analyticsToolboxName, { required })} />
      <Box mt={2}>
        <TextField
          id={analyticsToolboxName}
          size="small"
          error={!!error}
          required={required}
          value={analyticsToolbox}
          onChange={handleChange}
          label={<FormattedMessage id="connections.create.advanced.atLocation.title" />}
          helperText={
            <FormattedMessage
              id="connections.create.advanced.atLocation.description"
              values={{
                provider,
                a: (content: string) => <DocLink docUrl={providerDocLink}>{content}</DocLink>
              }}
            />
          }
        />
      </Box>
    </>
  )
}

export default AnalyticsToolboxLocationForm
